import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Switch,
  FormGroup,
  Checkbox,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  FormHelperText,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { apiClientCreateSchema, ApiClientCreateFormValues } from './apiClientCreateSchema';
import { useApiClientOptions, useCreateApiClient } from '../../../application/hooks/useApiClients';
import { ScopeGroup, CreateApiClientResult } from '../../../domain/models/ApiClient';
import { useNotification } from '../../../../../shared/contexts/useNotification';

// ── Design tokens (Nexus Admin) ──────────────────────────────────────────────

const colors = {
  surface: '#f8f9ff',
  onSurface: '#0b1c30',
  onSurfaceVariant: '#434656',
  outline: '#737688',
  outlineVariant: '#c3c5d9',
  surfaceContainerLow: '#eff4ff',
  surfaceContainerLowest: '#ffffff',
  primary: '#003ec7',
  onPrimary: '#ffffff',
  error: '#ba1a1a',
  errorContainer: '#ffdad6',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.surfaceContainerLow,
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    overflow: 'hidden',
    '& fieldset': {
      borderColor: colors.outlineVariant,
      borderRadius: '8px',
    },
    '&:hover fieldset': {
      borderColor: colors.outlineVariant,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.primary,
      borderWidth: '1px',
      boxShadow: '0 0 0 3px rgba(0, 76, 237, 0.15)',
    },
    '&.Mui-error fieldset': {
      borderColor: colors.error,
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    color: colors.outline,
    '&.Mui-focused': {
      color: colors.primary,
    },
    '&.Mui-error': {
      color: colors.error,
    },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    color: colors.outline,
    marginLeft: '4px',
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: colors.error,
  },
};

const switchSx = {
  '& .MuiSwitch-track': {
    backgroundColor: colors.outlineVariant,
    opacity: 1,
    borderRadius: '11px',
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: colors.surfaceContainerLowest,
    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
  },
  '& .Mui-checked': {
    '& .MuiSwitch-thumb': {
      backgroundColor: colors.surfaceContainerLowest,
    },
    '& + .MuiSwitch-track': {
      backgroundColor: colors.primary,
      opacity: 1,
    },
  },
};

const outlinedButtonSx = {
  borderColor: colors.outlineVariant,
  color: colors.onSurfaceVariant,
  borderRadius: '8px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.05em',
  '&:hover': {
    borderColor: colors.outlineVariant,
    backgroundColor: 'rgba(115, 118, 136, 0.05)',
  },
};

const primaryButtonSx = {
  bgcolor: colors.primary,
  color: colors.onPrimary,
  borderRadius: '8px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.05em',
  '&:hover': {
    bgcolor: 'rgba(0, 62, 199, 0.9)',
  },
};

const ghostPrimaryButtonSx = {
  borderColor: colors.primary,
  color: colors.primary,
  borderRadius: '8px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.05em',
  '&:hover': {
    borderColor: colors.primary,
    backgroundColor: 'rgba(0, 62, 199, 0.05)',
  },
};

// ── Hierarchical scope selector modal ────────────────────────────────────────

interface ScopeSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (scopes: string[]) => void;
  selected: string[];
  options: ScopeGroup[];
}

function ScopeSelectorModal({
  open,
  onClose,
  onConfirm,
  selected,
  options,
}: ScopeSelectorModalProps) {
  const [tempSelected, setTempSelected] = useState<string[]>(selected);

  // Sync with prop when opening
  React.useEffect(() => {
    if (open) setTempSelected(selected);
  }, [open, selected]);

  const handleParentToggle = (group: ScopeGroup) => {
    const allScopes = group.actions.map((action) => `${action}:${group.resource}`);
    const allSelected = allScopes.every((s) => tempSelected.includes(s));

    if (allSelected) {
      setTempSelected(tempSelected.filter((s) => !allScopes.includes(s)));
    } else {
      const added = allScopes.filter((s) => !tempSelected.includes(s));
      setTempSelected([...tempSelected, ...added]);
    }
  };

  const handleChildToggle = (scope: string) => {
    if (tempSelected.includes(scope)) {
      setTempSelected(tempSelected.filter((s) => s !== scope));
    } else {
      setTempSelected([...tempSelected, scope]);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '20px',
          fontWeight: 600,
          color: colors.onSurface,
        }}
      >
        Select Scopes
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {options.map((group) => {
          const allScopes = group.actions.map((action) => `${action}:${group.resource}`);
          const selectedInGroup = allScopes.filter((s) => tempSelected.includes(s));
          const allSelected = selectedInGroup.length === allScopes.length;
          const someSelected = selectedInGroup.length > 0 && !allSelected;

          return (
            <Accordion
              key={group.resource}
              disableGutters
              elevation={0}
              sx={{
                borderBottom: '1px solid',
                borderColor: colors.outlineVariant,
                '&:last-child': { borderBottom: 0 },
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleParentToggle(group);
                      }}
                      sx={{
                        color: colors.outlineVariant,
                        '&.Mui-checked, &.Mui-indeterminate': {
                          color: colors.primary,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: colors.onSurface,
                      }}
                    >
                      {group.resourceLabel}
                    </Typography>
                  }
                  onClick={(e) => e.stopPropagation()}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense sx={{ pl: 4 }}>
                  {group.actions.map((action) => {
                    const scope = `${action}:${group.resource}`;
                    return (
                      <ListItem key={scope} disablePadding>
                        <ListItemButton onClick={() => handleChildToggle(scope)} sx={{ py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              checked={tempSelected.includes(scope)}
                              sx={{
                                color: colors.outlineVariant,
                                '&.Mui-checked': {
                                  color: colors.primary,
                                },
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontFamily: "'Courier Prime', monospace",
                                  fontSize: '13px',
                                  color: colors.onSurfaceVariant,
                                }}
                              >
                                {scope}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={outlinedButtonSx}>
          Cancel
        </Button>
        <Button onClick={() => onConfirm(tempSelected)} variant="contained" sx={primaryButtonSx}>
          Apply ({tempSelected.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export const ApiClientCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const createApiClient = useCreateApiClient();
  const { data: options, isLoading: optionsLoading } = useApiClientOptions();

  const [successResult, setSuccessResult] = useState<CreateApiClientResult | null>(null);
  const [scopeModalOpen, setScopeModalOpen] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ApiClientCreateFormValues>({
    resolver: zodResolver(apiClientCreateSchema),
    defaultValues: {
      name: '',
      clientType: '',
      isConfidential: true,
      tenantId: '',
      description: '',
      scopes: [],
      grantTypes: [],
      tokenTtlSeconds: 3600,
      requiresPkce: false,
      refreshTokenTtlSeconds: undefined,
    },
  });

  const scopes = watch('scopes');
  const grantTypes = watch('grantTypes');

  const handleRemoveScope = (scopeToRemove: string) => {
    setValue(
      'scopes',
      scopes.filter((s) => s !== scopeToRemove),
    );
  };

  const handleGrantTypeToggle = (value: string) => {
    const updated = grantTypes.includes(value)
      ? grantTypes.filter((g) => g !== value)
      : [...grantTypes, value];
    setValue('grantTypes', updated);
  };

  const onSubmit = (values: ApiClientCreateFormValues) => {
    createApiClient.mutate(
      {
        name: values.name,
        clientType: values.clientType,
        isConfidential: values.isConfidential,
        tenantId: values.tenantId || undefined,
        description: values.description || undefined,
        scopes: values.scopes,
        grantTypes: values.grantTypes,
        tokenTtlSeconds: values.tokenTtlSeconds,
        requiresPkce: values.requiresPkce,
        refreshTokenTtlSeconds: values.refreshTokenTtlSeconds,
      },
      {
        onSuccess: (result) => setSuccessResult(result),
        onError: () => showNotification('Failed to create API client', 'error'),
      },
    );
  };

  const handleCopySecret = (text: string) => {
    navigator.clipboard.writeText(text);
    showNotification('Copied to clipboard', 'success');
  };

  const handleCloseSuccess = () => {
    setSuccessResult(null);
    navigate('/api-clients');
  };

  return (
    <Box sx={{ p: 5, maxWidth: 1000 }}>
      <Typography
        component="h1"
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '32px',
          fontWeight: 700,
          lineHeight: '40px',
          letterSpacing: '-0.02em',
          color: colors.onSurface,
          mb: '32px',
        }}
      >
        Add API Client
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={5}>
          {/* Basic Information */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '24px',
            }}
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Name"
                  fullWidth
                  required
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  sx={inputSx}
                />
              )}
            />

            <Controller
              name="clientType"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.clientType}
                  required
                  sx={inputSx}
                >
                  <InputLabel sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                    Client Type
                  </InputLabel>
                  <Select
                    {...field}
                    label="Client Type"
                    disabled={optionsLoading}
                    sx={{
                      backgroundColor: colors.surfaceContainerLow,
                      borderRadius: '8px',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      '& fieldset': {
                        borderColor: errors.clientType ? colors.error : colors.outlineVariant,
                        borderRadius: '8px',
                      },
                      '&:hover fieldset': {
                        borderColor: errors.clientType ? colors.error : colors.outlineVariant,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: colors.primary,
                        borderWidth: '1px',
                        boxShadow: '0 0 0 3px rgba(0, 76, 237, 0.15)',
                      },
                    }}
                  >
                    {(options?.clientTypes ?? []).map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.clientType && (
                    <FormHelperText sx={{ color: colors.error }}>
                      {errors.clientType.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Box>

          {/* Settings */}
          <Paper
            elevation={0}
            sx={{
              bgcolor: colors.surfaceContainerLowest,
              border: '1px solid',
              borderColor: colors.outlineVariant,
              borderRadius: '12px',
              p: 3,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '24px',
            }}
          >
            <Controller
              name="isConfidential"
              control={control}
              render={({ field }) => (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: colors.onSurface,
                      }}
                    >
                      Confidential client
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '12px',
                        color: colors.outline,
                      }}
                    >
                      Requires a client secret for token exchange
                    </Typography>
                  </Box>
                  <Switch checked={field.value} onChange={field.onChange} sx={switchSx} />
                </Box>
              )}
            />

            <Controller
              name="requiresPkce"
              control={control}
              render={({ field }) => (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: colors.onSurface,
                      }}
                    >
                      Require PKCE
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '12px',
                        color: colors.outline,
                      }}
                    >
                      Proof Key for Code Exchange (Highly recommended)
                    </Typography>
                  </Box>
                  <Switch checked={field.value} onChange={field.onChange} sx={switchSx} />
                </Box>
              )}
            />
          </Paper>

          {/* Identifiers & Description */}
          <Stack spacing={2}>
            <Controller
              name="tenantId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Tenant ID (optional)"
                  fullWidth
                  placeholder="UUID v7"
                  error={!!errors.tenantId}
                  helperText={errors.tenantId?.message ?? 'Leave empty for platform-global client'}
                  sx={inputSx}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Description (optional)"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  sx={inputSx}
                />
              )}
            />
          </Stack>

          {/* Timeouts */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '24px',
            }}
          >
            <Controller
              name="tokenTtlSeconds"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Token TTL (seconds)"
                  type="number"
                  fullWidth
                  required
                  error={!!errors.tokenTtlSeconds}
                  helperText={
                    errors.tokenTtlSeconds?.message ?? 'How long access tokens remain valid'
                  }
                  inputProps={{ min: 1 }}
                  sx={inputSx}
                />
              )}
            />

            <Controller
              name="refreshTokenTtlSeconds"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                  }
                  label="Refresh Token TTL (seconds, optional)"
                  type="number"
                  fullWidth
                  error={!!errors.refreshTokenTtlSeconds}
                  helperText={
                    errors.refreshTokenTtlSeconds?.message ??
                    'Leave empty to disable refresh tokens'
                  }
                  inputProps={{ min: 1 }}
                  sx={inputSx}
                />
              )}
            />
          </Box>

          {/* Scopes */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: colors.outlineVariant,
                pb: 1,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '20px',
                  fontWeight: 600,
                  lineHeight: '28px',
                  color: colors.onSurface,
                }}
              >
                Scopes
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setScopeModalOpen(true)}
                variant="outlined"
                disabled={optionsLoading}
                sx={ghostPrimaryButtonSx}
              >
                Add Scope
              </Button>
            </Box>

            {scopes.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  border: '1px dashed',
                  borderColor: colors.outlineVariant,
                  borderRadius: '12px',
                  bgcolor: colors.surfaceContainerLow,
                  p: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                <LockOpenIcon sx={{ fontSize: '48px', color: colors.outlineVariant, mb: 1 }} />
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    color: colors.onSurfaceVariant,
                  }}
                >
                  No scopes selected
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '12px',
                    color: colors.outline,
                    mt: 0.5,
                  }}
                >
                  Assign permissions to this client by adding scopes.
                </Typography>
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: colors.outlineVariant,
                  borderRadius: '12px',
                  bgcolor: colors.surfaceContainerLow,
                  p: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {scopes.map((scope) => (
                  <Chip
                    key={scope}
                    label={scope}
                    onDelete={() => handleRemoveScope(scope)}
                    size="small"
                    sx={{
                      fontFamily: "'Courier Prime', monospace",
                      fontSize: '13px',
                      bgcolor: colors.surfaceContainerLowest,
                      border: '1px solid',
                      borderColor: colors.outlineVariant,
                    }}
                  />
                ))}
              </Paper>
            )}

            {errors.scopes && (
              <FormHelperText
                error
                sx={{ mt: 1, fontFamily: 'Inter, sans-serif', fontSize: '12px' }}
              >
                {Array.isArray(errors.scopes)
                  ? (errors.scopes as Array<{ message?: string } | undefined>).find(
                      (e) => e?.message,
                    )?.message
                  : (errors.scopes as { message?: string })?.message}
              </FormHelperText>
            )}
          </Box>

          {/* Grant Types */}
          <Box>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '20px',
                fontWeight: 600,
                lineHeight: '28px',
                color: colors.onSurface,
                mb: 1.5,
              }}
            >
              Grant Types
            </Typography>
            <FormGroup row>
              {(options?.grantTypes ?? []).map(({ value, label }) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      checked={grantTypes.includes(value)}
                      onChange={() => handleGrantTypeToggle(value)}
                      size="small"
                      disabled={optionsLoading}
                      sx={{
                        color: colors.outlineVariant,
                        '&.Mui-checked': {
                          color: colors.primary,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        color: colors.onSurface,
                      }}
                    >
                      {label}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
            {errors.grantTypes && (
              <FormHelperText error sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>
                {(errors.grantTypes as { message?: string })?.message}
              </FormHelperText>
            )}
          </Box>

          {/* Server error */}
          {createApiClient.isError && (
            <Alert
              severity="error"
              sx={{
                bgcolor: colors.errorContainer,
                color: colors.error,
                borderRadius: '8px',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {createApiClient.error instanceof Error
                ? createApiClient.error.message
                : 'An unexpected error occurred'}
            </Alert>
          )}
        </Stack>

        {/* Actions */}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            mt: 5,
            pt: 5,
            borderTop: '1px solid',
            borderColor: colors.outlineVariant,
            justifyContent: 'flex-end',
          }}
        >
          <Button variant="outlined" onClick={() => navigate('/api-clients')} sx={outlinedButtonSx}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={createApiClient.isPending}
            startIcon={
              createApiClient.isPending ? (
                <CircularProgress size={16} sx={{ color: colors.onPrimary }} />
              ) : undefined
            }
            sx={primaryButtonSx}
          >
            {createApiClient.isPending ? 'Creating…' : 'Create API Client'}
          </Button>
        </Stack>
      </Box>

      {/* Scope Selector Modal */}
      <ScopeSelectorModal
        open={scopeModalOpen}
        onClose={() => setScopeModalOpen(false)}
        onConfirm={(updated) => {
          setValue('scopes', updated);
          setScopeModalOpen(false);
        }}
        selected={scopes}
        options={options?.scopes ?? []}
      />

      {/* Success dialog — shows client_id and secret (displayed once) */}
      <Dialog open={successResult !== null} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '20px',
            fontWeight: 600,
            color: colors.onSurface,
          }}
        >
          API Client Created
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: '8px',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Save the client secret now — it will not be shown again.
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                fontWeight: 500,
                color: colors.outline,
              }}
            >
              Client ID
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                sx={{
                  fontFamily: "'Courier Prime', monospace",
                  fontSize: '13px',
                  color: colors.onSurfaceVariant,
                }}
              >
                {successResult?.clientId}
              </Typography>
              <IconButton
                size="small"
                onClick={() => successResult && handleCopySecret(successResult.clientId)}
                sx={{ color: colors.primary }}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Stack>
          </Box>

          {successResult?.clientSecret && (
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: colors.outline,
                }}
              >
                Client Secret
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  sx={{
                    fontFamily: "'Courier Prime', monospace",
                    fontSize: '13px',
                    color: colors.onSurfaceVariant,
                    wordBreak: 'break-all',
                  }}
                >
                  {successResult.clientSecret}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    successResult?.clientSecret && handleCopySecret(successResult.clientSecret!)
                  }
                  sx={{ color: colors.primary }}
                >
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseSuccess} variant="contained" sx={primaryButtonSx}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
